import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import FaIcon from "../../utils/fa_icon";
import AppTile from "./app-tile";
import AppMotif from "./app-switch-motifs";
import {
  COMPLETE_AT,
  REVEAL_AT,
  STAGE_TIMINGS,
  SwitchableApp,
} from "./app-switch.types";
import styles from "./app-switch.module.scss";

const easeProgress = (t: number) => 1 - Math.pow(1 - Math.min(t, 1), 1.8);


export default function AppSwitchCurtain({
  from,
  to,
}: {
  from: SwitchableApp | null;
  to: SwitchableApp;
}) {
  const [stage, setStage] = useState(0);
  const [complete, setComplete] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const barRef = useRef<HTMLSpanElement>(null);
  const percentRef = useRef<HTMLSpanElement>(null);

  const stages = useMemo(
    () => [
      from ? `Leaving ${from.title}` : "Preparing your session",
      ...(to.stages || [
        `Loading ${to.title}`,
        "Applying your license & permissions",
        "Building your workspace",
      ]),
    ],
    [from, to.stages, to.title],
  );

  useEffect(() => {
    const timers = STAGE_TIMINGS.slice(1).map((delay, index) =>
      setTimeout(() => setStage(index + 1), delay),
    );
    timers.push(setTimeout(() => setComplete(true), COMPLETE_AT));
    timers.push(setTimeout(() => setRevealing(true), REVEAL_AT));
    return () => timers.forEach(clearTimeout);
  }, [to.key]);

  useEffect(() => {
    let frame = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const value = easeProgress((now - start) / COMPLETE_AT);
      const percent = Math.min(100, Math.round(value * 100));
      if (barRef.current) barRef.current.style.width = `${value * 100}%`;
      if (percentRef.current) percentRef.current.textContent = `${percent}%`;
      if (percent < 100) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to.key]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={classNames(styles.curtain, {
        [styles.curtain_revealing]: revealing,
      })}
      role="status"
      aria-live="polite"
      aria-label={`Switching to ${to.title}`}
      data-qa="app-switch-overlay"
      style={
        {
          ["--accent"]: to.accent || "var(--primary-color)",
          ["--accent-alt"]:
            to.accentAlt || to.accent || "var(--primary-color)",
        } as CSSProperties
      }
    >
      <AppMotif motif={to.motif} />
      <span className={styles.grain} />

      <div className={styles.curtain_content}>
        {/* From → To handoff */}
        <div className={styles.handoff}>
          {from && (
            <AppTile
              app={from}
              className={classNames(styles.handoff_tile, styles.handoff_from)}
              style={{ ["--accent"]: from.accent } as CSSProperties}
            />
          )}

          <span className={styles.handoff_trail} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>

          <span className={styles.handoff_target}>
            <span className={styles.pulse_ring} />
            <span className={styles.pulse_ring} />
            <span className={styles.tile_glow} />
            <AppTile
              app={to}
              className={classNames(styles.handoff_tile, styles.handoff_to, {
                [styles.handoff_to_settled]: complete,
              })}
            />
          </span>
        </div>

        <p className={styles.curtain_eyebrow}>
          {complete ? to.readyLabel || "Ready" : "Entering"}
        </p>
        <h2 className={styles.curtain_title}>
          <span className={styles.title_shimmer}>{to.title}</span>
        </h2>
        <p className={styles.curtain_tagline}>{to.tagline || to.description}</p>

        {/* Stage checklist */}
        <ul className={styles.stages}>
          {stages.map((label, index) => {
            const isDone = complete || index < stage;
            const isRunning = !complete && index === stage;

            return (
              <li
                key={label}
                className={classNames(styles.stage, {
                  [styles.stage_done]: isDone,
                  [styles.stage_running]: isRunning,
                })}
              >
                <span className={styles.stage_marker}>
                  {isDone ? (
                    <FaIcon icon={faCheck} />
                  ) : isRunning ? (
                    <span className={styles.stage_spinner} />
                  ) : (
                    <span className={styles.stage_dot} />
                  )}
                </span>
                <span className={styles.stage_label}>{label}</span>
              </li>
            );
          })}
        </ul>

        <div className={styles.progress_row}>
          <span className={styles.progress}>
            <span className={styles.progress_bar} ref={barRef} />
          </span>
          <span className={styles.progress_percent} ref={percentRef}>
            0%
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
